/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Clinic, LASER_SIGN } from './clinic.model';
import { ClinicState, ClinicStore } from './clinic.store';
import { Observable } from 'rxjs';
import { AesRequestModel } from '@core/models/AesRequest.model';
import { map, tap } from 'rxjs/operators';
import { Referrer } from '@core/models/Referrer';
import { Service } from '@core/models/Service';
import { Professional } from '@core/models/Professional';
import { Equipment } from '@core/models/Equipment';
import { ClinicQuery } from '@state/clinic/clinic.query';
import { SessionQuery } from '@state/session/session.query';
import { SettingsQuery } from '@state/settings/settings.query';
import moment from 'moment';
import { FLOW_TIME_FORMAT } from '@core/constants';
import { Cabinet } from '@core/models/Cabinet';
import { Pack } from '@core/models/Pack';
import { Tag } from '@core/models/Tag';
import { SessionService } from '@state/session/session.service';
import { ApiService } from '@core/services/api/api.service';
import {createDocumentClass} from '@core/models/DocumentClass';

@Injectable({ providedIn: 'root' })
export class ClinicService {
    private timeslotDuration = 1;

    constructor(
        private apiService: ApiService,
        private clinicQuery: ClinicQuery,
        private clinicStore: ClinicStore,
        private sessionQuery: SessionQuery,
        private sessionService: SessionService,
        private settingsQuery: SettingsQuery) {
    }

    initClinic(clinicId?: string): Observable<any> {
        const workingClinic: string = clinicId ?? this.clinicQuery.getActiveId().toString();
        this.timeslotDuration = this.settingsQuery.getValue().timeSlot;

        /*
         * TODO.CMM Esta optimización no se puede realizar porque hay una variable global en el back que une
         * la clínica activa con la sesión de modo que hay que llamar al SEC_CONFIG_CLISEL_FORM para que la settee.
         * Habría que crear un nuevo SEC para settear la id de la clínica
         */
        // const currentSelectedClinic = this.clinicQuery.getEntity(clinicId);
        //
        // if (currentSelectedClinic?.loaded) {
        //     this.selectClinic(workingClinic);
        //     return EMPTY;
        // }

        const form = new AesRequestModel('SEC_CONFIG_CLISEL_ME_FORM', [{
            Param: 'Json', Value: JSON.stringify({ clinicID: workingClinic }), AESIndex: '1'
        }], 0);

        return this.apiService.callApiObservable(form)
            .pipe(
                map(response => JSON.parse(response)),
                tap(({ data }: { data: Record<string, any> }) => {
                    const { appUser } = this.settingsQuery.getValue();

                    const referrers: Referrer[] = this.buildReferrers(data.ReferrerList);
                    const laserSign = Number(data.ClinicLaserSignEnabled) as LASER_SIGN;

                    const services: Service[] = [];
                    data.AppZoneList.forEach(service => {
                        services.push(this.buildService(service, false));
                    });

                    data.AppTreatmentList.forEach(service => {
                        services.push(this.buildService(service, true));
                    });

                    const packs: Pack[] = [];
                    data.PacksList?.forEach(pack => {
                        packs.push(this.buildPack(pack, services));
                    });

                    const professionals: Professional[] = this.buildProfessionals(data.ProfesionalList);

                    const equipmentList: Equipment[] = [];
                    data.EquipmentList.forEach(eq => {
                        const equipment = this.buildEquipment(eq);
                        if (equipment) {
                            equipmentList.push(equipment);
                        }
                    });

                    const cabinets: Cabinet[] = this.buildCabinets(data.CabinetList);
                    const tags: Tag[] = this.buildTags(data.TagsList);
                    const pinRequiredSecs = data?.SECPINList?.map(({ PermissionAction }) => PermissionAction) ?? [];

                    this.clinicStore.update(workingClinic, {
                        openSaturday: data.ClinicSat !== '0',
                        openSunday: data.ClinicSun !== '0',
                        equipmentList,
                        loaded: true,
                        packs,
                        professionals,
                        referrers,
                        services,
                        laserSign,
                        cabinets,
                        openingTime: moment(data.ClinicTimeStart, FLOW_TIME_FORMAT),
                        closingTime: moment(data.ClinicTimeEnd, FLOW_TIME_FORMAT),
                        weekdayOpeningTime: moment(data.ClinicWeekTimeStart, FLOW_TIME_FORMAT),
                        weekdayClosingTime: moment(data.ClinicWeekTimeEnd, FLOW_TIME_FORMAT),
                        weekendOpeningTime: moment(data.ClinicWeekendTimeStart, FLOW_TIME_FORMAT),
                        weekendClosingTime: moment(data.ClinicWeekendTimeEnd, FLOW_TIME_FORMAT),
                        tags,
                        pinRequiredSecs,
                        sectionsLocks: {
                            documents: {
                                isEnabled: data.ClinicSignLock === '-1',
                                code: data.ClinicSignLockCode,
                            },
                            clientEdit: {
                                isEnabled: data.ClinicNewClientLock === '-1',
                                code: data.ClinicNewClientLockCode,
                            }
                        },
                    });

                    this.update({
                        documentClasses: data.documentTypeList.map(createDocumentClass),
                    });

                    this.sessionService.update({
                        userRequirePin: data.PINRequired !== '0',
                        doctorMode: data.DoctorMode === '-1',
                        canViewClinicHistory: data.ViewClinicHistory === '-1',
                        canAddService: data.ErrLaserDetAdd === '0',
                        canDelService: data.ErrLaserDetDel === '0',
                        canAddSlots: data.ErrAppSlotsAdd === '0',
                        canDelSlots: data.ErrAppSlotsDel === '0'
                    });

                    this.selectClinic(workingClinic);
                })
            );
    }

    add(clinic: Clinic): void {
        this.clinicStore.add(clinic);
    }

    selectClinic(id: string): void {
        this.clinicStore.setActive(id);
    }

    update(clinicState: Partial<ClinicState>): void {
        this.clinicStore.update(clinicState);
    }

    updateEntity(id: string, clinic: Partial<Clinic>): void {
        this.clinicStore.update(id, clinic);
    }

    /**
     * Método público para generar cabinas desde fuera de este servicio
     * @param cabinets cabinas preformateadas
     * @returns cabinas
     */
    buildProfessionalCabinets(cabinets: any[]) {
        return this.buildCabinets(cabinets);
    }

    private buildEquipment(equipment: any): Equipment {
        if (equipment.equipdisabled === '0') {
            return {
                id: equipment.VALUE,
                name: equipment.equipmentCode + equipment.equipmentsn
            };
        }

        return null;
    }

    private buildProfessionals(professionalList: any[]): Professional[] {
        const listProfessional: Professional[] = [];

        professionalList.forEach(p => {
            listProfessional.push({
                id: p.userid,
                name: p.username,
                assistant: p.assistant !== '0',
                doctor: p.doctor !== '0',
            });
        });

        return listProfessional;
    }

    private buildReferrers(referrersList: any[]): Referrer[] {
        const listReferrers: Referrer[] = [];

        referrersList.forEach(r => {
            listReferrers.push({
                id: r.refererid,
                description: r.refererdesc,
                disabled: r.refererdisabled !== '0'
            });
        });

        return listReferrers;
    }

    private buildCabinets(cabinetsList: any[]): Cabinet[] {
        const cabinets: Cabinet[] = [];

        cabinetsList.filter(c => c.cabinetid !== '0').forEach(c => {
            cabinets.push({
                id: c.cabinetid,
                order: parseInt(c.cabinetorder, 10),
                name: c.cabinetname,
                abbr: c.cabinetabbrev,
                color: c.cabinetcolor,
                saturday: c.cabinetsat !== '-1',
                sunday: c.cabinetsun !== '-1'
            });
        });

        return cabinets;
    }

    //TODO: Yo creo que solo es necesario en este punto que es en el que se crean los servicios para luego poder añadirlos en la pantalla de cabina
    private buildService(data: any, isTreatment: boolean): Service {
        return {
            fromPack: '0',
            id: data.productid,
            group: data.productzonecode,
            name: data.productdesc,
            familyName: data.familyname ?? '',
            familyId: data.familyid ?? 0,
            time: moment.duration(data.duration * this.timeslotDuration, 'm'),
            slots: parseInt(data.duration, 10),
            isTreatment,
            requiresDoctor: data.requiresDoctor !== '0'
        } as Service;
    }

    private buildTags(tagsList: any): Tag[] {
        const tags: Tag[] = [];

        if (tagsList && tagsList.length > 0) {
            tagsList.forEach(t => {
                tags.push({
                    id: t.tagid,
                    description: t.tagdesc,
                    color: '#' + t.tagcode,
                    enabled: t.tagenabled === '-1' ? true : false,
                });
            });
        }

        return tags;
    }

    private buildPack(data: any, services: Service[]): Pack {
        const packServices = services
            .filter(service => data.packservices.split(',').includes(service.id))
            .map(service => ({ ...service, fromPack: data.packid }));

        return {
            id: data.packid,
            name: data.packname,
            familyId: data.packfamilyid,
            services: packServices
        };
    }
}
